import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CharacterCountBar } from "./CharacterCountBar";
import { H1_LIMITS, META_LIMITS, TITLE_LIMITS } from "./seoCharLimits";
import type {
	PageIssue,
	TitleMetaFix,
	H1Fix,
	TitleMetaDetails,
	H1Details,
} from "@/types/technicalAudit";

// Whitespace/case-insensitive heading comparison for the keep-this-one picker.
const normalizeHeading = (s: string) => s.replace(/\s+/g, " ").trim().toLowerCase();

interface TextDiffCardProps {
	issue: PageIssue;
	onEdit: (value: TitleMetaFix | H1Fix) => void;
	/** Lock the proposed-H1 editor while a promote-selection is staged above —
	 * two live "what Apply will do" inputs at once invites contradictory edits. */
	h1EditingDisabled?: boolean;
}

export function TextDiffCard({ issue, onEdit, h1EditingDisabled = false }: TextDiffCardProps) {
	const isTitleMeta = issue.issue_type === "title_meta";
	const isH1 = ["h1", "h1_missing", "h1_multiple", "h1_too_long"].includes(
		issue.issue_type,
	);
	const h1Details = isH1 ? (issue.issue_details as H1Details) : null;
	// The multiple-H1 fix DEMOTES extra <h1>s to <h2>; it never rewrites the
	// heading text (GH #594). The operator picks WHICH heading survives
	// (keep_h1). Hoisted above the draft so seeding + lifting can use it.
	const isMultipleH1Demote =
		issue.issue_type === "h1_multiple" ||
		(issue.issue_type === "h1" && h1Details?.multiple_h1s === true);
	const h1KeepOptions = useMemo(
		() => (isMultipleH1Demote ? (h1Details?.current_h1s ?? []) : []),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[issue.id, issue.issue_details, isMultipleH1Demote],
	);
	const storedFix = issue.approved_value || issue.proposed_fix;
	const draftKey = `${issue.id}:${issue.issue_type}:${JSON.stringify(storedFix ?? "")}`;
	const initialDraft = (() => {
		if (isTitleMeta && storedFix) {
			const fix = storedFix as TitleMetaFix;
			return {
				title: fix.new_seo_title || fix.new_title || "",
				meta: fix.new_meta_description || "",
				h1: "",
			};
		}
		if (isH1 && storedFix) {
			const fix = storedFix as H1Fix;
			if (isMultipleH1Demote) {
				// Demote case: the draft holds the heading to KEEP. An explicit
				// keep_h1 wins; else a stored suggestion whose text matches one
				// of the page's H1s preselects that option; else no pick yet.
				const stored = (fix.keep_h1 || fix.new_h1 || "").trim();
				const match = stored
					? h1KeepOptions.find(
							(o) => normalizeHeading(o) === normalizeHeading(stored),
						)
					: undefined;
				return {
					title: "",
					meta: "",
					h1: fix.keep_h1 ? stored : (match ?? ""),
				};
			}
			return { title: "", meta: "", h1: fix.new_h1 || "" };
		}
		return { title: "", meta: "", h1: "" };
	})();

	// Initialize edited values
	const [draft, setDraft] = useState<{
		key: string;
		title: string;
		meta: string;
		h1: string;
	} | null>(null);
	const editedTitle = draft?.key === draftKey ? draft.title : initialDraft.title;
	const editedMeta = draft?.key === draftKey ? draft.meta : initialDraft.meta;
	const editedH1 = draft?.key === draftKey ? draft.h1 : initialDraft.h1;

	// Update parent when edited values change
	useEffect(() => {
		if (isTitleMeta) {
			const t = editedTitle.trim() || undefined;
			// Canonical SEO/meta title key + legacy alias + target stamp so the
			// backend apply chokepoint confirms this is a SEO title tag write.
			onEdit({
				new_seo_title: t,
				new_title: t,
				target_field: "seo_title",
				new_meta_description: editedMeta.trim() || undefined,
			});
		} else if (isH1) {
			if (isMultipleH1Demote) {
				// keep_h1 is the applier's explicit survivor signal; only lift
				// it when the choice really is one of the page's H1s. new_h1
				// rides along for the before/after display paths.
				const chosen = editedH1.trim();
				const matched =
					chosen.length > 0 &&
					h1KeepOptions.some(
						(o) => normalizeHeading(o) === normalizeHeading(chosen),
					);
				onEdit({
					new_h1: editedH1,
					keep_h1: matched ? chosen : undefined,
				});
			} else {
				onEdit({
					new_h1: editedH1,
				});
			}
		}
	}, [
		editedTitle,
		editedMeta,
		editedH1,
		isTitleMeta,
		isH1,
		isMultipleH1Demote,
		h1KeepOptions,
		onEdit,
	]);

	if (isTitleMeta) {
		const details = issue.issue_details as TitleMetaDetails;
		const proposedFix = issue.proposed_fix as TitleMetaFix | null;

		return (
			<div className="space-y-6">
				{/* Title Section */}
				{(details.title_missing ||
					details.title_too_short ||
					details.title_too_long ||
					proposedFix?.new_seo_title ||
					proposedFix?.new_title) && (
					<Card>
						<CardHeader>
							<CardTitle className="text-base">SEO Title Tag</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							{/* Current Value */}
							<div>
								<div className="mb-2 text-sm font-medium text-muted-foreground">
									Current
								</div>
								<div className="rounded-md border bg-muted/50 p-3">
									<p className="text-sm">
										{details.current_title || (
											<span className="italic text-muted-foreground">
												(empty)
											</span>
										)}
									</p>
								</div>
								{details.current_title && (
									<div className="mt-2">
										<CharacterCountBar
											current={details.title_length || 0}
											min={TITLE_LIMITS.min}
											max={TITLE_LIMITS.max}
											optimal={TITLE_LIMITS.optimal}
										/>
									</div>
								)}
							</div>

							{/* Proposed Fix */}
							{(proposedFix?.new_seo_title || proposedFix?.new_title) && (
								<div>
									<div className="mb-2 text-sm font-medium text-green-600 dark:text-green-400">
										Proposed Fix (AI-generated)
									</div>
									<p className="mb-2 text-xs text-muted-foreground">
										Apply writes this as the page's SEO title tag.
									</p>
									<Textarea
										value={editedTitle}
										onChange={(e) =>
											setDraft({
												key: draftKey,
												title: e.target.value,
												meta: editedMeta,
												h1: editedH1,
											})
										}
										className="min-h-[80px]"
										placeholder="Edit the proposed SEO title tag..."
									/>
									<div className="mt-2">
										<CharacterCountBar
											current={editedTitle.trim().length}
											min={TITLE_LIMITS.min}
											max={TITLE_LIMITS.max}
											optimal={TITLE_LIMITS.optimal}
										/>
									</div>
								</div>
							)}
						</CardContent>
					</Card>
				)}

				{/* Meta Description Section */}
				{(details.meta_missing ||
					details.meta_too_short ||
					details.meta_too_long ||
					proposedFix?.new_meta_description) && (
					<Card>
						<CardHeader>
							<CardTitle className="text-base">Meta Description</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							{/* Current Value */}
							<div>
								<div className="mb-2 text-sm font-medium text-muted-foreground">
									Current
								</div>
								<div className="rounded-md border bg-muted/50 p-3">
									<p className="text-sm">
										{details.current_meta_description || (
											<span className="italic text-muted-foreground">
												(empty)
											</span>
										)}
									</p>
								</div>
								{details.current_meta_description && (
									<div className="mt-2">
										<CharacterCountBar
											current={details.meta_length || 0}
											min={META_LIMITS.min}
											max={META_LIMITS.max}
											optimal={META_LIMITS.optimal}
										/>
									</div>
								)}
							</div>

							{/* Proposed Fix */}
							{proposedFix?.new_meta_description && (
								<div>
									<div className="mb-2 text-sm font-medium text-green-600 dark:text-green-400">
										Proposed Fix (AI-generated)
									</div>
									<p className="mb-2 text-xs text-muted-foreground">
										Apply writes this as the page's meta description.
									</p>
									<Textarea
										value={editedMeta}
										onChange={(e) =>
											setDraft({
												key: draftKey,
												title: editedTitle,
												meta: e.target.value,
												h1: editedH1,
											})
										}
										className="min-h-[100px]"
										placeholder="Edit the proposed meta description..."
									/>
									<div className="mt-2">
										<CharacterCountBar
											current={editedMeta.trim().length}
											min={META_LIMITS.min}
											max={META_LIMITS.max}
											optimal={META_LIMITS.optimal}
										/>
									</div>
								</div>
							)}
						</CardContent>
					</Card>
				)}
			</div>
		);
	}

	if (isH1) {
		const details = issue.issue_details as H1Details;
		const proposedFix = issue.proposed_fix as H1Fix | null;
		// `current_h1s` is not guaranteed (see H1Details): the DFS h1_missing detector
		// emits no heading list at all, and some rows carry the singular `current_h1`.
		// Indexing it unguarded crashed the whole drawer.
		const currentH1 = details.current_h1s?.[0] || details.current_h1 || "";

		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-base">
						{/* For a MISSING H1 this card is the "insert a brand-new heading"
						    option (vs. promoting an existing one above); for the other H1
						    issue types it edits the H1 that is already there. */}
						{details.h1_missing ? "Create a new H1 Heading instead" : "H1 Heading"}
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					{/* Current Value */}
					<div>
						<div className="mb-2 text-sm font-medium text-muted-foreground">
							Current
						</div>
						<div className="rounded-md border bg-muted/50 p-3">
							{details.h1_missing ? (
								<p className="italic text-sm text-muted-foreground">
									(missing)
								</p>
							) : details.multiple_h1s ? (
								<div className="space-y-2">
									{(details.current_h1s ?? []).map((h1, idx) => {
										const isKept =
											isMultipleH1Demote &&
											editedH1.trim().length > 0 &&
											normalizeHeading(h1) === normalizeHeading(editedH1);
										const showMark =
											isMultipleH1Demote &&
											editedH1.trim().length > 0 &&
											h1KeepOptions.some(
												(o) =>
													normalizeHeading(o) ===
													normalizeHeading(editedH1),
											);
										return (
											// biome-ignore lint/suspicious/noArrayIndexKey: H1 list order is stable and meaningful
											<div
												key={idx}
												className={`flex items-start gap-2 rounded-md p-2 ${
													isKept
														? "border border-teal/40 bg-teal-light/40"
														: ""
												}`}
											>
												<p className="flex-1 text-sm">
													{idx + 1}. {h1}
													{showMark && (
														<span
															className={`ml-2 text-xs ${
																isKept
																	? "font-medium text-teal-dark"
																	: "text-muted-foreground"
															}`}
														>
															{isKept ? "keep as H1" : "demote to H2"}
														</span>
													)}
												</p>
												{isMultipleH1Demote && (
													<Button
														type="button"
														variant="outline"
														size="sm"
														data-testid={`h1-keep-pick-${idx}`}
														className="shrink-0"
														disabled={isKept}
														onClick={() =>
															setDraft({
																key: draftKey,
																title: editedTitle,
																meta: editedMeta,
																h1,
															})
														}
													>
														{isKept ? "Keeping" : "Keep this one"}
													</Button>
												)}
											</div>
										);
									})}
									<p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
										Warning: Multiple H1 tags found ({details.h1_count})
									</p>
									{isMultipleH1Demote && (
										<p className="text-xs text-muted-foreground">
											Pick the heading to keep as the page's H1 — the other
											H1 tag{(details.h1_count ?? 2) > 2 ? "s are" : " is"}{" "}
											demoted to H2.
										</p>
									)}
								</div>
							) : (
								<p className="text-sm">{currentH1}</p>
							)}
						</div>
						{/* Length only matters for a rewrite. In the multiple-H1 case the
						    problem is the COUNT of H1s, not their length, so the char bar
						    (which read "too long") is misleading noise — hide it. */}
						{!details.h1_missing && !isMultipleH1Demote && currentH1 && (
							<div className="mt-2">
								<CharacterCountBar
									current={currentH1.length}
									min={H1_LIMITS.min}
									max={H1_LIMITS.max}
									optimal={H1_LIMITS.optimal}
								/>
							</div>
						)}
					</div>

					{/* Proposed Fix */}
					{(proposedFix?.new_h1 ||
						(isMultipleH1Demote && editedH1.trim().length > 0)) &&
						(isMultipleH1Demote ? (
							/* Demote case: state what actually happens instead of an
							   editable "new H1" + char diff (GH #594). */
							<div>
								<div className="mb-2 text-sm font-medium text-muted-foreground">
									What the fix does
								</div>
								<div className="rounded-md border bg-muted/50 p-3 space-y-1">
									<p className="text-sm">
										{editedH1.trim() ? (
											<>
												Keeps{" "}
												<span className="font-medium">
													"{editedH1.trim()}"
												</span>{" "}
												as the canonical H1 and demotes the other H1 tag
												{(details.h1_count ?? 2) > 2 ? "s" : ""} to{" "}
												<span className="font-medium">H2</span>{" "}
												(subheadings).
											</>
										) : (
											<>
												Keeps one canonical{" "}
												<span className="font-medium">H1</span> and demotes
												the extra H1 tag
												{(details.h1_count ?? 2) > 2 ? "s" : ""} to{" "}
												<span className="font-medium">H2</span>{" "}
												(subheadings).
											</>
										)}
									</p>
									<p className="text-xs text-muted-foreground">
										The heading text is not changed — only its level. This
										leaves a single H1 on the page.
									</p>
								</div>
								{proposedFix?.reasoning && (
									<div className="mt-3 rounded-md bg-blue-50 dark:bg-blue-950/40 p-3">
										<p className="text-xs font-medium text-blue-800 dark:text-blue-300">
											AI Reasoning:
										</p>
										<p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
											{proposedFix.reasoning}
										</p>
									</div>
								)}
							</div>
						) : (
							<div className={h1EditingDisabled ? "opacity-50" : undefined}>
								<div className="mb-2 text-sm font-medium text-green-600 dark:text-green-400">
									Proposed Fix (AI-generated)
								</div>
								{h1EditingDisabled && (
									<p className="mb-2 text-xs font-medium text-amber-700 dark:text-amber-400">
										Disabled — a heading above is selected for promotion. Clear
										that selection to use this generated heading instead.
									</p>
								)}
								<p className="mb-2 text-xs text-muted-foreground">
									{details.h1_missing
										? "Apply adds this as a new H1 heading at the top of the page — the page currently has none. (If a ‘Use a heading already on the page’ list is shown above, picking one promotes it in place instead of adding a new heading.)"
										: "Apply replaces the page's H1 with this heading."}
								</p>
								<Textarea
									value={editedH1}
									onChange={(e) =>
										setDraft({
											key: draftKey,
											title: editedTitle,
											meta: editedMeta,
											h1: e.target.value,
										})
									}
									className="min-h-[80px]"
									placeholder="Edit the proposed H1..."
									disabled={h1EditingDisabled}
								/>
								<div className="mt-2">
									<CharacterCountBar
										current={editedH1.length}
										min={H1_LIMITS.min}
										max={H1_LIMITS.max}
										optimal={H1_LIMITS.optimal}
									/>
								</div>
								{proposedFix.reasoning && (
									<div className="mt-3 rounded-md bg-blue-50 dark:bg-blue-950/40 p-3">
										<p className="text-xs font-medium text-blue-800 dark:text-blue-300">
											AI Reasoning:
										</p>
										<p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
											{proposedFix.reasoning}
										</p>
									</div>
								)}
							</div>
						))}
				</CardContent>
			</Card>
		);
	}

	return null;
}
